// Finds the Parvekshak device on the LAN so Local Mode's "Local App" tab can
// open the full application UI at its 192.168.x.x address, instead of just
// the ad-hoc RTSP camera view.
//
// This mirrors the health-check semantics parvekshak_webview already uses
// natively (_parvekshakHealthy in main.dart) — same /health probe, same
// 503-is-still-healthy rule, same JSON content-type check to reject router
// captive portals that return 200 for any path. What it can't mirror is the
// scan itself: a browser has no API to read its own subnet or open raw
// sockets, so instead of a full 254-host sweep we probe the small set of
// default home-router subnets in parallel, and otherwise ask the user for
// the IP directly (their router's admin page always knows it).

const CACHE_KEY   = 'prvsk_local_server_url';
const COMMON_SUBNETS = ['192.168.0', '192.168.1'];
const HEALTH_TIMEOUT_MS = 1000;
const SCAN_BUDGET_MS    = 4000;

// fetch() to a plain http:// target from an https page is blocked outright as
// mixed content — silently (the promise just rejects), with no workaround via
// fetch/XHR/<img> in any modern browser. That makes checkLocalServer() and
// discoverLocalServer() below unusable whenever this page itself loaded over
// https (e.g. the installed PWA at app.parvekshak.in). The one thing that
// ISN'T blocked is a full top-level navigation to http:// — callers on a
// secure origin should skip straight to that instead of probing first.
export function isSecureOrigin() {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
}

function normalizeBase(input) {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
  // :8765 is the box's raw API (JSON, not the app) — the UI is nginx on :80.
  return withScheme.replace(/\/+$/, '').replace(/:8765$/, '');
}

// On a box, nginx serves this React app on :80 and only proxies
// /api/aiservice/* to Parvekshak — a bare /health there returns index.html
// (text/html), which the JSON check below rejects. That's why discovery and
// manual entry never matched a real box. Probe the proxied path first; bare
// /health stays for a direct :8765 / dev target.
const HEALTH_PATHS = ['/api/aiservice/health', '/health'];

async function probe(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// Resolves to the working base URL (e.g. "http://192.168.1.42") or null.
export async function checkLocalServer(hostOrUrl) {
  const base = normalizeBase(hostOrUrl);
  if (!base) return null;
  for (const path of HEALTH_PATHS) {
    let res;
    try { res = await probe(`${base}${path}`); } catch { return null; } // nothing listening
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) continue;
    if (res.status === 503 || res.ok) return base; // 503: server up, cameras offline — still valid
  }
  return null;
}

export function getCachedLocalServerUrl() {
  return localStorage.getItem(CACHE_KEY) || '';
}

export function setCachedLocalServerUrl(url) {
  if (url) localStorage.setItem(CACHE_KEY, url);
  else      localStorage.removeItem(CACHE_KEY);
}

// ── Native bridge (Android app) ──────────────────────────────────────────────
// Inside the app none of the above is needed: parvekshak_webview scans the
// phone's real Wi-Fi subnet natively, checks the box over plain http (no
// mixed-content block there), remembers its IP + MAC so it can re-find it
// after a DHCP change, and switches the WebView to it. See the
// FlutterLocalServer channel in parvekshak_webview/lib/main.dart. Every call
// resolves to { url, mac } or null.

export function isNativeLocalServerAvailable() {
  return typeof window !== 'undefined' && !!window.FlutterLocalServer;
}

let _nativeSeq = 0;
const _nativePending = new Map();

function nativeCall(action, url = '') {
  if (!isNativeLocalServerAvailable()) return Promise.resolve(null);
  window.__onLocalServerReply = (id, result) => {
    const resolve = _nativePending.get(id);
    _nativePending.delete(id);
    if (resolve) resolve(result || null);
  };
  const id = ++_nativeSeq;
  return new Promise((resolve) => {
    _nativePending.set(id, resolve);
    window.FlutterLocalServer.postMessage(JSON.stringify({ id, action, url }));
  });
}

export const nativeSavedLocalServer    = ()    => nativeCall('saved');
export const nativeDiscoverLocalServer = ()    => nativeCall('discover');
// Switches the whole app to the box; resolves to null if it can't be reached.
export const nativeOpenLocalServer     = (url) => nativeCall('open', url);

// Cached URL first (fast path on a device that's connected before), then a
// parallel probe of 192.168.0.0/24 + 192.168.1.0/24. Resolves to a working
// base URL, or null if nothing answered within the scan budget — callers
// should fall back to asking the user for an IP.
export async function discoverLocalServer() {
  const cached = getCachedLocalServerUrl();
  if (cached) {
    const ok = await checkLocalServer(cached);
    if (ok) return ok;
  }

  const targets = [];
  for (const subnet of COMMON_SUBNETS) {
    for (let i = 1; i <= 254; i++) targets.push(`${subnet}.${i}`);
  }

  return Promise.race([
    new Promise((resolve) => {
      let pending = targets.length;
      let settled = false;
      targets.forEach((host) => {
        checkLocalServer(host).then((found) => {
          if (settled) return;
          if (found) { settled = true; resolve(found); return; }
          if (--pending === 0) { settled = true; resolve(null); }
        });
      });
    }),
    new Promise((resolve) => setTimeout(() => resolve(null), SCAN_BUDGET_MS)),
  ]);
}
